/** @format */

import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableHead,
    TableRow,
    Chip,
    Stack,
} from "@mui/material";
import {
    ArrowBack,
    MailOutline,
    PhoneOutlined,
    BusinessOutlined,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchCustomer,
    fetchInvoices,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectCurrentCustomer,
    selectCustomersError,
    selectInvoices,
    selectIsLoadingInvoices,
} from "../../redux/selectors/collectSelector";
import { colors } from "../../utility/types/constant";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectStatusChip,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectGhostButton,
    CollectInfoBanner,
    FlexBetween,
} from "./styles";

function formatMoney(value: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
        }).format(value || 0);
    } catch {
        return `$${(value || 0).toFixed(2)}`;
    }
}

function formatDate(value?: string): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: "UTC",
        });
    } catch {
        return value;
    }
}

const CollectCustomerDetail: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { customerId } = useParams<{ customerId: string }>();

    const businessId = useSelector(selectActiveBusinessId);
    const customer = useSelector(selectCurrentCustomer);
    const error = useSelector(selectCustomersError);
    const invoices = useSelector(selectInvoices);
    const isLoadingInvoices = useSelector(selectIsLoadingInvoices);

    useEffect(() => {
        if (!businessId || !customerId) return;
        dispatch(fetchCustomer({ customerId }));
        // Filter invoices to this customer.
        dispatch(fetchInvoices({ customerId, limit: 100 }));
    }, [dispatch, businessId, customerId]);

    // Split invoices: outstanding (status !== paid && !== void) vs closed.
    const { outstanding, closed } = useMemo(() => {
        const out: typeof invoices = [];
        const cls: typeof invoices = [];
        invoices.forEach((inv) => {
            if (inv.status === "paid" || inv.status === "void") cls.push(inv);
            else out.push(inv);
        });
        return { outstanding: out, closed: cls };
    }, [invoices]);

    if (!customer) {
        return (
            <CollectPageWrapper>
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress sx={{ color: colors.mainSecondary }} />
                </Box>
            </CollectPageWrapper>
        );
    }

    return (
        <CollectPageWrapper>

            <Box
                sx={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    padding: { xs: "20px 12px", md: "32px 24px" },
                }}
            >
                <CollectGhostButton
                    onClick={() => navigate("/collect/customers")}
                    startIcon={<ArrowBack fontSize="small" />}
                    sx={{ mb: 2 }}
                >
                    Back to customers
                </CollectGhostButton>

                {error && (
                    <CollectInfoBanner tone="warn">
                        <span>{error}</span>
                    </CollectInfoBanner>
                )}

                {/* Header card */}
                <CollectSectionCard>
                    <FlexBetween>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 800,
                                    fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)",
                                    color: colors.primary,
                                }}
                            >
                                {customer.displayName}
                            </Typography>
                            {customer.companyName &&
                                customer.companyName !== customer.displayName && (
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            color: "#6b7280",
                                            fontSize: "0.9rem",
                                            mt: 0.5,
                                        }}
                                    >
                                        <BusinessOutlined
                                            sx={{
                                                fontSize: 16,
                                                verticalAlign: "middle",
                                                mr: 0.7,
                                            }}
                                        />
                                        {customer.companyName}
                                    </Typography>
                                )}
                            {/* Stage 2F — communication opt-out chips. We
                                show both email- and SMS-level flags so the
                                operator knows at a glance why a customer
                                might not be receiving reminders. */}
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                                {customer.communicationOptedOut && (
                                    <Chip
                                        size="small"
                                        color="warning"
                                        label="Email opted out"
                                    />
                                )}
                                {customer.smsOptedOut && (
                                    <Chip
                                        size="small"
                                        color="warning"
                                        label={`SMS opted out${customer.smsOptedOutKeyword
                                            ? ` (${customer.smsOptedOutKeyword})`
                                            : ""
                                            }`}
                                    />
                                )}
                            </Stack>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "0.72rem",
                                    color: "#6b7280",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                }}
                            >
                                Open balance
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "1.7rem",
                                    fontWeight: 800,
                                    color:
                                        (customer.balance || 0) > 0
                                            ? colors.primary
                                            : "#6b7280",
                                }}
                            >
                                {formatMoney(customer.balance || 0, customer.currency)}
                            </Typography>
                        </Box>
                    </FlexBetween>

                    <Grid container spacing={2} sx={{ mt: 1.5 }}>
                        {customer.email && (
                            <Grid item xs={12} sm={6}>
                                <ContactRow
                                    icon={<MailOutline fontSize="small" />}
                                    label="Email"
                                    value={customer.email}
                                />
                            </Grid>
                        )}
                        {customer.phone && (
                            <Grid item xs={12} sm={6}>
                                <ContactRow
                                    icon={<PhoneOutlined fontSize="small" />}
                                    label="Phone"
                                    value={customer.phone}
                                />
                            </Grid>
                        )}
                    </Grid>
                </CollectSectionCard>

                {/* Outstanding invoices */}
                <CollectSectionCard>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            color: colors.primary,
                            mb: 1.5,
                        }}
                    >
                        Outstanding invoices ({outstanding.length})
                    </Typography>
                    {isLoadingInvoices ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress
                                size={24}
                                sx={{ color: colors.mainSecondary }}
                            />
                        </Box>
                    ) : outstanding.length === 0 ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>All caught up</CollectEmptyTitle>
                            <CollectEmptyHint>
                                This customer has no open invoices right now.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <InvoiceTable invoices={outstanding} onRow={(id) => navigate(`/collect/invoices/${id}`)} />
                    )}
                </CollectSectionCard>

                {/* Closed invoices */}
                {closed.length > 0 && (
                    <CollectSectionCard>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                color: colors.primary,
                                mb: 1.5,
                            }}
                        >
                            Paid & closed ({closed.length})
                        </Typography>
                        <InvoiceTable invoices={closed} onRow={(id) => navigate(`/collect/invoices/${id}`)} />
                    </CollectSectionCard>
                )}
            </Box>
        </CollectPageWrapper>
    );
};

const ContactRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string;
}> = ({ icon, label, value }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ color: "#6b7280" }}>{icon}</Box>
        <Box>
            <Typography
                sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "0.72rem",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "0.9rem",
                    color: "#1f2937",
                }}
            >
                {value}
            </Typography>
        </Box>
    </Box>
);

const InvoiceTable: React.FC<{
    invoices: any[];
    onRow: (id: string) => void;
}> = ({ invoices, onRow }) => (
    <CollectTableContainer>
        <Table>
            <TableHead>
                <TableRow>
                    <CollectTableHeadCell>Invoice #</CollectTableHeadCell>
                    <CollectTableHeadCell>Issued</CollectTableHeadCell>
                    <CollectTableHeadCell>Due</CollectTableHeadCell>
                    <CollectTableHeadCell align="right">Total</CollectTableHeadCell>
                    <CollectTableHeadCell align="right">Open</CollectTableHeadCell>
                    <CollectTableHeadCell>Status</CollectTableHeadCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {invoices.map((inv) => (
                    <TableRow
                        key={inv._id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => onRow(inv._id)}
                    >
                        <CollectTableBodyCell>
                            <strong>#{inv.docNumber || "—"}</strong>
                        </CollectTableBodyCell>
                        <CollectTableBodyCell>
                            {formatDate(inv.txnDate || inv.issueDate)}
                        </CollectTableBodyCell>
                        <CollectTableBodyCell>
                            {formatDate(inv.dueDate)}
                        </CollectTableBodyCell>
                        <CollectTableBodyCell align="right">
                            {formatMoney(inv.totalAmount, inv.currency)}
                        </CollectTableBodyCell>
                        <CollectTableBodyCell align="right">
                            <strong>{formatMoney(inv.balance, inv.currency)}</strong>
                        </CollectTableBodyCell>
                        <CollectTableBodyCell>
                            <CollectStatusChip
                                label={inv.status.replace("_", " ")}
                                statusKey={inv.status}
                                size="small"
                            />
                        </CollectTableBodyCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </CollectTableContainer>
);

export default CollectCustomerDetail;
